/**
 * MoneyRow — an amount, when it is the thing being recognised.
 *
 * ONE ANATOMY FOR EVERY MONEY ROW ON THIS CANVAS. A bank transaction and an unassigned deposit are
 * the same shape — an amount, the day it moved, and a ragged string somebody else wrote — so they
 * are drawn by one component rather than by two sets of near-identical cells that drift the moment
 * one of them is touched. The deposit pool adds a fourth cell, its age, and nothing else differs.
 *
 * THE AMOUNT IS THE PRIMARY VALUE, so it is the one thing here that cannot be clipped. It LEADS, in
 * a column of its own, right-aligned on tabular figures so the pesewas of nine rows line up down
 * one edge, and it is `flex: none`: nothing the bank wrote can take width from it. The date and the
 * age are bounded strings and take fixed columns behind it. The META — remarks, or whatever the
 * bank called it — is the only unbounded string on the line, so it is the cell that absorbs what is
 * left and the cell that ellipses.
 *
 * THIS IS THE EXACT INVERSION OF WHAT BOTH ROWS USED TO DO. Drawn as a label and a `.sd-pop-m`
 * meta, the meta sized itself first: `ATM WD GTB000000000079 ACC…` took the line and the amount
 * beside it read `GH₵5,0…`, and the deposit pool showed `GH₵5,000.…` against a date, an age and a
 * free-text remark carrying somebody's name and phone number. The amount is what the eye is
 * scanning for; it is now the last thing that can give.
 *
 * EVERY ROW HAS THE SAME ANATOMY whatever its meta costs, which is the property that makes a list
 * of nine payments scannable at all: the amounts share an edge, the dates share an edge, and the
 * ragged text is the last thing on the line rather than the thing setting the layout.
 *
 * THE META CARRIES ITS OWN `title` AS WELL AS THE ROW'S. The row's tooltip is the whole line and is
 * what a pointer anywhere on the row gets; the cell's is the ragged text alone, which is the only
 * thing here that can be longer than its column, and it is what a pointer that went straight to the
 * ellipsis is asking for.
 */

interface Props {
    /** `GH₵5,000.00` — pre-formatted, because only the caller knows the currency. */
    amount: string;
    /** `Jun 13, 2026`, or the caller's own word for a row that has no date. */
    dated: string;
    /** `51 days in the pool` — the deposit pool's fourth cell. Omitted where a row has no age. */
    age?: string;
    /** Remarks, or the bank's own description — the one cell that gives way. */
    meta: string;
}

export default function MoneyRow({ amount, dated, age, meta }: Props) {
    return (
        <span className="sd-mrow">
            <span className="sd-mrow-amt">{amount}</span>
            <span className="sd-mrow-date">{dated}</span>
            {age ? <span className="sd-mrow-age">{age}</span> : null}
            <span className="sd-mrow-meta" title={meta}>
                {meta}
            </span>
        </span>
    );
}
